<template>
  <b-modal
    centered
    v-if="isOpen"
    v-model="isOpen"
    cancel-disabled
    hide-footer
    :title="`Acesso ao usuário - ${user.email}`"
    size="lg"
    @close="close"
  >
    <b-row>
      <b-col cols="12">
        <b-alert variant="info" show fade class="mb-3">
          <i class="fas fa-info-circle mr-1"></i> Para conseguir acessar a conta
          do usuário desejado, será configurado uma senha de confirmação. O link
          de acesso terá uma validade de 1h, após este período será necessário
          gerar um novo link.
        </b-alert>
        <b-alert variant="warning" show fade class="mb-3">
          <i class="fas fa-exclamation-triangle mr-1"></i> <b>Atenção:</b> É
          recomendado abrir o link de autenticação em um outro navegador ou
          mesmo numa guia anônima, para evitar conflitos de sessão. Caso abra o
          link no mesmo navegador, termine as ações que necessitam ser salvas e
          faça o logout da sua conta antes de acessar o link gerado.
        </b-alert>
      </b-col>
      <b-col cols="12">
        <b-form-group label-for="password">
          <InputPassword
            placeholder="Digite a senha de verificação de acesso"
            aria-describedby="password-help-block"
            :value.sync="fields.password"
            :disabled="success"
            @update:value="validateFields"
            :state="
              !fields.password ? null : !errorMessages.password ? true : false
            "
          />
          <b-form-invalid-feedback v-if="errorMessages.password" :state="false">
            Digite uma senha válida. A senha é obrigatória.
          </b-form-invalid-feedback>
          <b-form-text v-else id="password-help-block">
            Lembre-se de guardar esta senha, ela será necessária para acessar a
            conta do usuário na tela de login.
          </b-form-text>
        </b-form-group>
      </b-col>
      <b-col class="mt-4">
        <b-button
          variant="outline-danger"
          @click="close"
          class="mr-2"
          :disabled="loading"
        >
          <i class="fas fa-times mr-2"></i> Fechar
        </b-button>
        <b-button
          variant="success"
          @click="impersonate"
          class="mr-2"
          :disabled="success || loading"
        >
          <i class="fas fa-check mr-2"></i> Solicitar acesso
          <b-spinner small class="ml-2" v-if="loading"></b-spinner>
        </b-button>
      </b-col>
      <b-col cols="12" v-if="success">
        <b-jumbotron
          bg-variant="light"
          border-variant="success"
          text-variant="dark"
          class="mt-3"
        >
          <template #header>
            <h1 class="display-5 text-success">
              <i class="fas fa-check-circle"></i> Sucesso!
            </h1>
          </template>
          <div class="lead">
            <div>
              O seu link de acesso foi gerado com sucesso! Acesse-o e confirme a
              senha de acesso, para assim acessar a conta do usuário.
            </div>
            <div class="fs-12 text-secondary">
              Lembre-se de abrir o link em um outro navegador ou guia anônima,
              para evitar conflitos de sessão.
            </div>
            <div class="fs-12 text-secondary">
              O link se expira em: {{ expiresAt.toLocaleString('pt-BR') }}
            </div>
            <div class="mt-4">
              <b-button variant="success" @click="copyLink">
                <i class="far fa-copy mr-2"></i> Copiar link
              </b-button>
            </div>
            <div class="fs-14 mt-4">
              ou copie e cole o link abaixo no seu navegador:
              <a :href="fullRedirectLink" target="_blank">
                {{ baseUrl }}/admin/impersonate/login?token={{ accessToken }}
              </a>
            </div>
          </div>
        </b-jumbotron>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import api from '@/api';
import makeToast from 'Mixins/makeToast';
import InputPassword from 'Components/Inputs/InputPassword.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'AccessUserModal',
  mixins: [makeToast],
  data() {
    return {
      loading: false,
      isOpen: false,
      accessToken: '',
      /** @type {Date|null} */
      expiresAt: null,
      user: {},
      fields: {
        password: '',
      },
      errorMessages: {
        password: '',
      },
    };
  },
  computed: {
    ...mapGetters(['getToken']),
    success() {
      return !!this.accessToken && !!this.expiresAt;
    },
    baseUrl() {
      return window.location.origin;
    },
    fullRedirectLink() {
      return `${this.baseUrl}/admin/impersonate/login?token=${this.accessToken}`;
    },
  },
  methods: {
    open(user) {
      this.isOpen = true;
      this.user = user;
    },
    close() {
      this.isOpen = false;
      this.user = {};
      this.fields.password = '';
      this.errorMessages.password = '';
      this.accessToken = '';
      this.expiresAt = null;
    },
    async impersonate() {
      this.loading = true;

      try {
        if (!this.validateFields()) return;

        const response = await api.post(
          `/v1/admin/impersonate`,
          {
            targetUserId: this.user.id,
            password: this.fields.password,
          },
          {
            headers: {
              Authorization: `Bearer ${this.getToken}`,
            },
          }
        );
        const { token, expiresAt } = response.data;

        if (!token || !expiresAt) {
          this.makeToast(
            'Erro',
            'Houve um erro ao solicitar o acesso ao usuário',
            'danger'
          );
          return;
        }

        this.accessToken = token;
        this.expiresAt = new Date(expiresAt);
      } catch (error) {
        const { message } = error.response.data || {};

        this.makeToast(
          'Erro',
          `Houve um erro ao solicitar o acesso ao usuário "${
            this.user.email
          }": "${message || error.message}"`,
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
    validateFields() {
      let isValid = true;

      if (!this.fields.password) {
        this.errorMessages.password = 'A senha é obrigatória';
        isValid = false;
      } else {
        this.errorMessages.password = '';
      }

      return isValid;
    },
    redirectToAccess() {
      window.open(this.fullRedirectLink, '_blank');
    },
    async copyLink() {
      await navigator.clipboard.writeText(this.fullRedirectLink);
      this.makeToast('Sucesso', 'Link copiado com sucesso', 'success');
    },
  },
  components: {
    InputPassword,
  },
};
</script>
